import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';
import { Num } from '../primitives/Num.tsx';

/**
 * A stage funnel, as rails rather than as a table.
 *
 * Each rail is drawn against the largest value in the set, so the bar says
 * "this stage next to the others" and nothing more - it is never a share of a
 * total, because the stages of a pipeline do not add up to one.
 *
 * The value is written out beside every rail. A bar on its own communicates
 * "quite a lot" and no reader can tell 173,500 from 213,000 by looking at two
 * lengths, so the bar is the shape of the data and the number is the data.
 */

export interface FunnelRow {
  id: string;
  label: ReactNode;
  /** The number the rail is drawn from. */
  weight: number;
  /** What is written beside the rail. */
  value: ReactNode;
  /** A second, quieter figure - a count beside a total. */
  aside?: ReactNode;
  /** Drawn in the ink rather than the accent: an outcome, not a stage. */
  settled?: boolean;
  /** Screen-level hooks - `data-stage`, and whatever else a caller names. */
  attrs?: Record<string, string>;
}

export function Funnel({
  rows,
  className,
  ...rest
}: { rows: readonly FunnelRow[]; className?: string } & Record<string, unknown>): JSX.Element {
  const peak = rows.reduce((most, row) => Math.max(most, row.weight), 0);
  return (
    <ul {...rest} data-component="Funnel" className={cx('flex flex-col gap-2.5', className)}>
      {rows.map((row) => (
        <li key={row.id} data-funnel-row={row.id} {...row.attrs} className="min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="min-w-0 truncate text-meta text-ink">{row.label}</span>
            <span className="flex shrink-0 items-baseline gap-2">
              {row.aside === undefined ? null : (
                <Num className="text-micro text-ink-meta">{row.aside}</Num>
              )}
              <Num className="text-meta text-ink-600">{row.value}</Num>
            </span>
          </div>
          <div className="mt-1 h-1 rounded-rail bg-sunken">
            <i
              data-funnel-fill={row.id}
              style={{ inlineSize: peak <= 0 ? '0%' : `${String((row.weight / peak) * 100)}%` }}
              className={cx('block h-full rounded-rail', row.settled ? 'bg-ink-400' : 'bg-bronze')}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
